import PptxGenJS from 'pptxgenjs';
import type { Factor, FactorsData } from '@shared/factors';
import type { Phase, RoadmapItem } from '@shared/roadmap';
import type { ScoreSummary, Verdict } from './scoring';

interface PptxData {
  clientName: string;
  diagnosticDate: string;
  summary: ScoreSummary;
  factorsData: FactorsData;
  scoreMap: Map<number, { level: number | null; memo: string | null }>;
  lv1Factors: Factor[];
  lv2Factors: Factor[];
  strongFactors: Factor[];
  roadmapItems: RoadmapItem[];
  includeRoadmap: boolean;
}

interface PptxOptions {
  filename: string;
}

const COLORS = {
  brand: '1A3A6E',
  accent: '378ADD',
  white: 'FFFFFF',
  textPrimary: '1F2937',
  textSecondary: '6B7280',
  rank1: 'C42B2B',
  rank2: 'BA7517',
  rank3: '888780',
  rank4: '5DCAA5',
  rank5: '0F6E56',
  bgRed: 'FEE2E2',
  bgOrange: 'FFEDD5',
  bgGreen: 'D1FAE5',
} as const;

const FONT = 'Meiryo';

const VERDICT_LABEL: Record<Verdict, string> = {
  A: 'A判定 優良',
  B: 'B判定 標準以上',
  C: 'C判定 改善必要',
  D: 'D判定 危機的',
};

const VERDICT_COLOR: Record<Verdict, string> = {
  A: COLORS.rank5,
  B: COLORS.rank4,
  C: COLORS.rank2,
  D: COLORS.rank1,
};

const PHASE_LABEL: Record<Phase, string> = {
  immediate: '即応(1ヶ月以内・優先度高)',
  short_term: '短期(3ヶ月以内・優先度中)',
  mid_term: '中期(6ヶ月以内・優先度通常)',
};

const PHASE_TITLE: Record<Phase, string> = {
  immediate: '改善ロードマップ ― 即応',
  short_term: '改善ロードマップ ― 短期',
  mid_term: '改善ロードマップ ― 中期',
};

const PHASE_COLOR: Record<Phase, string> = {
  immediate: COLORS.rank1,
  short_term: COLORS.rank2,
  mid_term: COLORS.rank5,
};

const PHASE_BG: Record<Phase, string> = {
  immediate: COLORS.bgRed,
  short_term: COLORS.bgOrange,
  mid_term: COLORS.bgGreen,
};

const LEVEL_LABEL: Record<number, string> = {
  1: 'Lv.1 危機',
  2: 'Lv.2 要改善',
  4: 'Lv.4 良好',
  5: 'Lv.5 最良',
};

const LEVEL_COLOR: Record<number, string> = {
  1: COLORS.rank1,
  2: COLORS.rank2,
  3: COLORS.rank3,
  4: COLORS.rank4,
  5: COLORS.rank5,
};

const ROADMAP_ITEMS_PER_SLIDE = 4;

function sanitizeFilename(name: string): string {
  return name.replace(/[\\/?%*:|"<>]/g, '_').replace(/\s+/g, '_').slice(0, 120);
}

function addFooter(slide: PptxGenJS.Slide, clientName: string, date: string, pageInfo?: string) {
  slide.addText(
    [
      { text: `${clientName} ・ 診断日 ${date}`, options: { color: COLORS.textSecondary } },
      ...(pageInfo
        ? [{ text: '   ' + pageInfo, options: { color: COLORS.textSecondary } }]
        : []),
    ],
    {
      x: 0.3,
      y: 5.3,
      w: 9.4,
      h: 0.25,
      fontFace: FONT,
      fontSize: 9,
      color: COLORS.textSecondary,
      align: 'left',
    },
  );
  slide.addText('Structive 採用力診断', {
    x: 0.3,
    y: 5.3,
    w: 9.4,
    h: 0.25,
    fontFace: FONT,
    fontSize: 9,
    color: COLORS.textSecondary,
    align: 'right',
  });
}

function addHeader(slide: PptxGenJS.Slide, title: string) {
  slide.addText(title, {
    x: 0.4,
    y: 0.25,
    w: 9.2,
    h: 0.55,
    fontFace: FONT,
    fontSize: 22,
    bold: true,
    color: COLORS.brand,
  });
  slide.addShape('line' as PptxGenJS.SHAPE_NAME, {
    x: 0.4,
    y: 0.85,
    w: 9.2,
    h: 0,
    line: { color: COLORS.accent, width: 1 },
  });
}

export async function exportReportToPptx(data: PptxData, options: PptxOptions): Promise<void> {
  const pres = new PptxGenJS();
  pres.layout = 'LAYOUT_16x9';
  pres.author = 'Structive';
  pres.title = `${data.clientName} 採用力診断レポート`;

  // Slide 1: Cover
  buildCoverSlide(pres, data);

  // Slide 2: Summary
  buildSummarySlide(pres, data);

  // Slide 3: Layer scores (radar)
  buildLayerScoreSlide(pres, data);

  // Slide 4: Category breakdown (bar)
  buildCategorySlide(pres, data);

  // Slide 5: Lv.1 crisis (if any)
  if (data.lv1Factors.length > 0) {
    buildFactorListSlide(pres, data, {
      title: 'Lv.1 危機項目 ― 即時対応が必要',
      factors: data.lv1Factors,
      accentColor: COLORS.rank1,
      bgColor: COLORS.bgRed,
    });
  }

  // Slide 6: Lv.2 needs improvement (if any)
  if (data.lv2Factors.length > 0) {
    buildFactorListSlide(pres, data, {
      title: 'Lv.2 要改善項目',
      factors: data.lv2Factors,
      accentColor: COLORS.rank2,
      bgColor: COLORS.bgOrange,
    });
  }

  // Slide 7: Lv.4-5 strengths (if any)
  if (data.strongFactors.length > 0) {
    buildFactorListSlide(pres, data, {
      title: 'Lv.4-5 強み ― 採用訴求素材',
      factors: data.strongFactors,
      accentColor: COLORS.rank5,
      bgColor: COLORS.bgGreen,
    });
  }

  // Slides 8-10: Roadmap (immediate / short_term / mid_term)
  if (data.includeRoadmap && data.roadmapItems.length > 0) {
    for (const phase of ['immediate', 'short_term', 'mid_term'] as Phase[]) {
      const items = data.roadmapItems.filter((i) => i.phase === phase);
      if (items.length === 0) continue;
      const totalPages = Math.ceil(items.length / ROADMAP_ITEMS_PER_SLIDE);
      for (let pageIdx = 0; pageIdx < totalPages; pageIdx++) {
        const start = pageIdx * ROADMAP_ITEMS_PER_SLIDE;
        const slice = items.slice(start, start + ROADMAP_ITEMS_PER_SLIDE);
        const pageInfo =
          totalPages > 1 ? `(${pageIdx + 1}/${totalPages})` : undefined;
        buildRoadmapPhaseSlide(pres, data, phase, slice, pageInfo);
      }
    }
  }

  // Final slide: Next steps
  buildNextStepsSlide(pres, data);

  await pres.writeFile({ fileName: `${sanitizeFilename(options.filename)}.pptx` });
}

// ---- Slide builders ----

function buildCoverSlide(pres: PptxGenJS, data: PptxData) {
  const slide = pres.addSlide();
  slide.background = { color: COLORS.brand };

  slide.addText('採用力診断レポート', {
    x: 0.5,
    y: 1.2,
    w: 9,
    h: 0.7,
    fontFace: FONT,
    fontSize: 32,
    bold: true,
    color: COLORS.white,
    align: 'center',
  });

  slide.addText(data.clientName, {
    x: 0.5,
    y: 2.3,
    w: 9,
    h: 1.3,
    fontFace: FONT,
    fontSize: 40,
    bold: true,
    color: COLORS.white,
    align: 'center',
  });

  slide.addText(`診断日: ${data.diagnosticDate}`, {
    x: 0.5,
    y: 3.9,
    w: 9,
    h: 0.4,
    fontFace: FONT,
    fontSize: 16,
    color: 'B8C4D6',
    align: 'center',
  });

  slide.addShape('rect' as PptxGenJS.SHAPE_NAME, {
    x: 0.5,
    y: 5.0,
    w: 1.4,
    h: 0.45,
    fill: { color: COLORS.white },
    line: { type: 'none' },
  });
  slide.addText('Structive', {
    x: 0.5,
    y: 5.0,
    w: 1.4,
    h: 0.45,
    fontFace: 'Inter',
    fontSize: 14,
    bold: true,
    color: COLORS.brand,
    align: 'center',
    valign: 'middle',
  });
}

function buildSummarySlide(pres: PptxGenJS, data: PptxData) {
  const slide = pres.addSlide();
  addHeader(slide, '総合評価');

  const { summary } = data;
  const total = summary.total;
  const verdict = summary.verdict;

  // Total score (large)
  slide.addText(total !== null ? total.toFixed(1) : '-', {
    x: 0.5,
    y: 1.2,
    w: 3.8,
    h: 2.2,
    fontFace: 'Inter',
    fontSize: 96,
    bold: true,
    color: COLORS.brand,
    align: 'center',
    valign: 'middle',
  });
  slide.addText('/ 5.0', {
    x: 0.5,
    y: 3.3,
    w: 3.8,
    h: 0.4,
    fontFace: 'Inter',
    fontSize: 20,
    color: COLORS.textSecondary,
    align: 'center',
  });
  slide.addText(`${summary.filledCount}/${summary.totalCount} 因子入力済`, {
    x: 0.5,
    y: 3.8,
    w: 3.8,
    h: 0.3,
    fontFace: FONT,
    fontSize: 11,
    color: COLORS.textSecondary,
    align: 'center',
  });

  // Verdict badge
  if (verdict) {
    slide.addShape('roundRect' as PptxGenJS.SHAPE_NAME, {
      x: 4.7,
      y: 1.4,
      w: 4.6,
      h: 0.9,
      fill: { color: VERDICT_COLOR[verdict] },
      line: { type: 'none' },
      rectRadius: 0.08,
    });
    slide.addText(VERDICT_LABEL[verdict], {
      x: 4.7,
      y: 1.4,
      w: 4.6,
      h: 0.9,
      fontFace: FONT,
      fontSize: 24,
      bold: true,
      color: COLORS.white,
      align: 'center',
      valign: 'middle',
    });
  }

  // Distribution
  slide.addText('レベル分布', {
    x: 4.7,
    y: 2.5,
    w: 4.6,
    h: 0.3,
    fontFace: FONT,
    fontSize: 12,
    bold: true,
    color: COLORS.textPrimary,
  });
  const dist = summary.distribution;
  const distItems: { label: string; count: number; color: string }[] = [
    { label: 'Lv.1 危機', count: dist.lv1, color: COLORS.rank1 },
    { label: 'Lv.2 要改善', count: dist.lv2, color: COLORS.rank2 },
    { label: 'Lv.3 標準', count: dist.lv3, color: COLORS.rank3 },
    { label: 'Lv.4-5 良好以上', count: dist.lv4_5, color: COLORS.rank5 },
  ];
  distItems.forEach((d, i) => {
    const y = 2.85 + i * 0.4;
    slide.addShape('rect' as PptxGenJS.SHAPE_NAME, {
      x: 4.7,
      y,
      w: 0.18,
      h: 0.28,
      fill: { color: d.color },
      line: { type: 'none' },
    });
    slide.addText(d.label, {
      x: 4.95,
      y,
      w: 3.5,
      h: 0.28,
      fontFace: FONT,
      fontSize: 12,
      color: COLORS.textPrimary,
      valign: 'middle',
    });
    slide.addText(String(d.count), {
      x: 8.5,
      y,
      w: 0.8,
      h: 0.28,
      fontFace: 'Inter',
      fontSize: 13,
      bold: true,
      color: COLORS.textPrimary,
      align: 'right',
      valign: 'middle',
    });
  });

  addFooter(slide, data.clientName, data.diagnosticDate);
}

function buildLayerScoreSlide(pres: PptxGenJS, data: PptxData) {
  const slide = pres.addSlide();
  addHeader(slide, 'レイヤー別の強み・弱み');

  const layers = data.factorsData.layers;
  const labels = layers.map((l) => `${l.id} ${l.name}`);
  const values = layers.map((l) => data.summary.byLayer[l.id] ?? 0);

  slide.addChart(
    pres.ChartType.radar,
    [{ name: 'スコア', labels, values }],
    {
      x: 0.5,
      y: 1.0,
      w: 5.0,
      h: 4.0,
      radarStyle: 'standard',
      showLegend: false,
      catAxisLabelFontFace: FONT,
      catAxisLabelFontSize: 10,
      valAxisMinVal: 0,
      valAxisMaxVal: 5,
      valAxisMajorUnit: 1,
      chartColors: [COLORS.accent],
      lineDataSymbol: 'circle',
      lineSize: 2,
    },
  );

  // Side panel: 4 layer scores
  slide.addText('各レイヤーの平均スコア', {
    x: 5.8,
    y: 1.0,
    w: 3.8,
    h: 0.3,
    fontFace: FONT,
    fontSize: 12,
    bold: true,
    color: COLORS.textPrimary,
  });
  layers.forEach((l, i) => {
    const score = data.summary.byLayer[l.id];
    const y = 1.45 + i * 0.65;
    slide.addText(`${l.id} ${l.name}`, {
      x: 5.8,
      y,
      w: 2.6,
      h: 0.35,
      fontFace: FONT,
      fontSize: 12,
      color: COLORS.textPrimary,
      valign: 'middle',
    });
    slide.addText(score !== null ? score.toFixed(1) : '-', {
      x: 8.5,
      y,
      w: 1.0,
      h: 0.35,
      fontFace: 'Inter',
      fontSize: 18,
      bold: true,
      color: COLORS.brand,
      align: 'right',
      valign: 'middle',
    });
    slide.addText(l.description.slice(0, 60), {
      x: 5.8,
      y: y + 0.32,
      w: 3.7,
      h: 0.28,
      fontFace: FONT,
      fontSize: 9,
      color: COLORS.textSecondary,
    });
  });

  addFooter(slide, data.clientName, data.diagnosticDate);
}

function buildCategorySlide(pres: PptxGenJS, data: PptxData) {
  const slide = pres.addSlide();
  addHeader(slide, '中分類別の詳細スコア');

  const entries = Object.entries(data.summary.byCategory);
  // Reverse so the first category is at the top of horizontal bar chart
  const labels = entries.map(([key, v]) => `${key} ${v.name}`).reverse();
  const values = entries.map(([, v]) => v.score ?? 0).reverse();

  slide.addChart(
    pres.ChartType.bar,
    [{ name: 'スコア', labels, values }],
    {
      x: 0.4,
      y: 1.0,
      w: 9.2,
      h: 4.1,
      barDir: 'bar',
      showLegend: false,
      catAxisLabelFontFace: FONT,
      catAxisLabelFontSize: 8,
      valAxisLabelFontFace: 'Inter',
      valAxisLabelFontSize: 9,
      valAxisMinVal: 0,
      valAxisMaxVal: 5,
      valAxisMajorUnit: 1,
      chartColors: [COLORS.accent],
      showValue: true,
      dataLabelFontFace: 'Inter',
      dataLabelFontSize: 8,
      dataLabelColor: COLORS.textPrimary,
      dataLabelFormatCode: '0.0',
      dataLabelPosition: 'outEnd',
    },
  );

  addFooter(slide, data.clientName, data.diagnosticDate);
}

function buildFactorListSlide(
  pres: PptxGenJS,
  data: PptxData,
  args: {
    title: string;
    factors: Factor[];
    accentColor: string;
    bgColor: string;
  },
) {
  const slide = pres.addSlide();
  addHeader(slide, `${args.title}(${args.factors.length}件)`);

  // Limit to 8 items per slide; if more, paginate
  const ITEMS_PER_SLIDE = 8;
  const pages = Math.ceil(args.factors.length / ITEMS_PER_SLIDE);

  for (let p = 0; p < pages; p++) {
    const slidePage = p === 0 ? slide : pres.addSlide();
    if (p > 0) {
      addHeader(
        slidePage,
        `${args.title}(${args.factors.length}件) (${p + 1}/${pages})`,
      );
    }

    const slice = args.factors.slice(p * ITEMS_PER_SLIDE, (p + 1) * ITEMS_PER_SLIDE);
    slice.forEach((f, idx) => {
      const score = data.scoreMap.get(f.id);
      const level = score?.level;
      const y = 1.05 + idx * 0.5;

      // Left accent bar
      slidePage.addShape('rect' as PptxGenJS.SHAPE_NAME, {
        x: 0.4,
        y,
        w: 0.08,
        h: 0.42,
        fill: { color: args.accentColor },
        line: { type: 'none' },
      });

      // Factor info text
      slidePage.addText(
        [
          { text: `#${f.id} `, options: { color: COLORS.textSecondary, fontSize: 10 } },
          { text: f.name, options: { color: COLORS.textPrimary, fontSize: 12, bold: true } },
          {
            text: `   ${f.layer_name} / ${f.category_name}`,
            options: { color: COLORS.textSecondary, fontSize: 9 },
          },
        ],
        {
          x: 0.6,
          y,
          w: 7.5,
          h: 0.42,
          fontFace: FONT,
          valign: 'middle',
        },
      );

      // Level badge on the right
      if (level) {
        slidePage.addShape('roundRect' as PptxGenJS.SHAPE_NAME, {
          x: 8.3,
          y: y + 0.05,
          w: 1.2,
          h: 0.32,
          fill: { color: LEVEL_COLOR[level] },
          line: { type: 'none' },
          rectRadius: 0.04,
        });
        slidePage.addText(LEVEL_LABEL[level] ?? `Lv.${level}`, {
          x: 8.3,
          y: y + 0.05,
          w: 1.2,
          h: 0.32,
          fontFace: FONT,
          fontSize: 9,
          bold: true,
          color: COLORS.white,
          align: 'center',
          valign: 'middle',
        });
      }
    });

    addFooter(
      slidePage,
      data.clientName,
      data.diagnosticDate,
      pages > 1 ? `${p + 1}/${pages}` : undefined,
    );
  }
}

function buildRoadmapPhaseSlide(
  pres: PptxGenJS,
  data: PptxData,
  phase: Phase,
  items: RoadmapItem[],
  pageInfo?: string,
) {
  const slide = pres.addSlide();
  addHeader(slide, PHASE_TITLE[phase] + (pageInfo ? ` ${pageInfo}` : ''));

  // Phase label band
  slide.addShape('rect' as PptxGenJS.SHAPE_NAME, {
    x: 0.4,
    y: 1.0,
    w: 9.2,
    h: 0.4,
    fill: { color: PHASE_BG[phase] },
    line: { type: 'none' },
  });
  slide.addShape('rect' as PptxGenJS.SHAPE_NAME, {
    x: 0.4,
    y: 1.0,
    w: 0.08,
    h: 0.4,
    fill: { color: PHASE_COLOR[phase] },
    line: { type: 'none' },
  });
  slide.addText(PHASE_LABEL[phase], {
    x: 0.6,
    y: 1.0,
    w: 9.0,
    h: 0.4,
    fontFace: FONT,
    fontSize: 12,
    bold: true,
    color: PHASE_COLOR[phase],
    valign: 'middle',
  });

  // Items
  const startY = 1.55;
  const itemHeight = 0.85;

  items.forEach((it, idx) => {
    const y = startY + idx * itemHeight;
    // Header line
    slide.addText(
      [
        { text: `#${it.factor_id} `, options: { color: COLORS.textSecondary, fontSize: 10 } },
        {
          text: it.factor_name,
          options: { color: COLORS.textPrimary, fontSize: 12, bold: true },
        },
        {
          text: `   Lv.${it.level} ${
            it.level === 1 ? '危機' : it.level === 2 ? '要改善' : it.level === 4 ? '良好' : '最良'
          }`,
          options: { color: PHASE_COLOR[phase], fontSize: 10, bold: true },
        },
      ],
      {
        x: 0.4,
        y,
        w: 9.2,
        h: 0.3,
        fontFace: FONT,
      },
    );
    // Action body
    slide.addText(it.action, {
      x: 0.4,
      y: y + 0.3,
      w: 9.2,
      h: itemHeight - 0.35,
      fontFace: FONT,
      fontSize: 10,
      color: COLORS.textPrimary,
    });
  });

  addFooter(slide, data.clientName, data.diagnosticDate, pageInfo);
}

function buildNextStepsSlide(pres: PptxGenJS, data: PptxData) {
  const slide = pres.addSlide();
  addHeader(slide, '次のステップ');

  const dist = data.summary.distribution;
  const bullets: string[] = [];
  if (dist.lv1 > 0) {
    bullets.push(`【最優先】Lv.1 危機項目 ${dist.lv1}件への即時対応(1ヶ月以内)`);
  }
  if (dist.lv2 > 0) {
    bullets.push(`【高優先】Lv.2 要改善項目 ${dist.lv2}件の改善計画策定(3ヶ月以内)`);
  }
  if (dist.lv4_5 > 0) {
    bullets.push(
      `【攻め】Lv.4-5 強み ${dist.lv4_5}件を採用訴求素材として求人票・採用サイト・SNSに展開`,
    );
  }
  bullets.push('月次で採用ファネル(応募・面接・内定・入社)の数値計測と改善サイクルを定着');
  bullets.push('四半期ごとに本診断を再実施し、スコアの推移を経営会議で共有');

  bullets.forEach((b, i) => {
    slide.addText(b, {
      x: 0.6,
      y: 1.2 + i * 0.65,
      w: 9.0,
      h: 0.55,
      fontFace: FONT,
      fontSize: 14,
      color: COLORS.textPrimary,
      bullet: { type: 'bullet', code: '25A0' },
      paraSpaceAfter: 6,
    });
  });

  // Closing message
  slide.addShape('rect' as PptxGenJS.SHAPE_NAME, {
    x: 0.4,
    y: 4.7,
    w: 9.2,
    h: 0.7,
    fill: { color: COLORS.bgGreen },
    line: { type: 'none' },
  });
  slide.addText(
    '「採用は経営課題」 ― ストラクティブ社が伴走支援します',
    {
      x: 0.4,
      y: 4.7,
      w: 9.2,
      h: 0.7,
      fontFace: FONT,
      fontSize: 14,
      bold: true,
      color: COLORS.rank5,
      align: 'center',
      valign: 'middle',
    },
  );

  addFooter(slide, data.clientName, data.diagnosticDate);
}
